import React, { useState } from 'react';
import styled from 'styled-components';
import { colors } from '../../styles/colors';

// ========================================
// STYLED COMPONENTS
// ========================================

const Container = styled.div`
  position: relative;
  width: 100%;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

const StyledInput = styled.input`
  padding: 14px 48px 14px 16px;
  border-radius: 8px;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  width: 100%;
  box-sizing: border-box;
  outline: none;

  /* login = écran auth ; app = formulaires (suit --ucp-* / thème app) */
  ${props => props.$variant === 'login' ? `
    background: rgba(255, 255, 255, 0.1) !important;
    color: white;
    border: 2px solid rgba(255, 255, 255, 0.1);

    &::placeholder {
      color: rgba(255, 255, 255, 0.6);
    }

    &:focus {
      border-color: rgb(52, 152, 219);
      box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.2);
      transform: translateY(-2px);
    }

    &:hover:not(:focus) {
      border-color: rgba(255, 255, 255, 0.3);
      transform: translateY(-1px);
    }

    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-webkit-autofill:active {
      -webkit-box-shadow: 0 0 0 1000px transparent inset !important;
      -webkit-text-fill-color: white !important;
      background-color: transparent !important;
      background: rgba(255, 255, 255, 0.1) !important;
      transition: background-color 5000s ease-in-out 0s;
    }
  ` : `
    background: var(--ucp-input-bg) !important;
    color: var(--ucp-text);
    border: 2px solid var(--ucp-input-border);

    &::placeholder {
      color: var(--ucp-text-muted);
    }

    &:focus {
      border-color: var(--ucp-input-focus-border);
      box-shadow: 0 0 0 3px var(--ucp-input-focus-shadow);
    }

    &:hover:not(:focus) {
      border-color: var(--ucp-input-border);
    }

    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-webkit-autofill:active {
      -webkit-box-shadow: 0 0 0 1000px var(--ucp-input-bg) inset !important;
      -webkit-text-fill-color: var(--ucp-text) !important;
      background-color: var(--ucp-input-bg) !important;
      transition: background-color 5000s ease-in-out 0s;
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    padding: 12px 42px 12px 14px;
    font-size: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 11px 40px 11px 13px;
    font-size: 0.95rem;
  }
`;

const ToggleButton = styled.button`
  position: absolute;
  right: 12px;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 4px;
  width: 28px;
  height: 28px;
  
  /* 🎨 COULEUR selon le theme */
  ${props => props.$variant === 'login' ? `
    color: rgba(255, 255, 255, 0.7);
    
    &:hover:not(:disabled) {
      color: white;
      transform: scale(1.1);
    }
  ` : `
    color: var(--ucp-text-muted);
    
    &:hover:not(:disabled) {
      color: var(--ucp-input-focus-border);
      transform: scale(1.1);
    }
  `}
  
  svg {
    width: 22px;
    height: 22px;
    stroke: currentColor;
    fill: none;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  
  &:active:not(:disabled) {
    transform: scale(0.95);
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.3;
  }
  
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
  
  @media (max-width: 768px) {
    right: 10px;
    width: 26px;
    height: 26px;
    
    svg {
      width: 20px;
      height: 20px;
    }
  }
  
  @media (max-width: 480px) {
    right: 8px;
    width: 24px;
    height: 24px;
    
    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const ErrorText = styled.span`
  position: absolute;
  top: calc(100% + 4px);
  left: 4px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.buttonDanger};
  display: flex;
  align-items: center;
  gap: 6px;
  animation: slideDown 0.2s ease-out;
  background: rgba(231, 76, 60, 0.1);
  padding: 4px 8px;
  border-radius: 4px;
  
  &:before {
    content: '⚠️';
    font-size: 14px;
  }
  
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

// ========================================
// COMPOSANT PASSWORDINPUT
// ========================================

/**
 * PasswordInput - Composant de saisie mot de passe avec toggle visibilité
 * 
 * Fonctionnalités :
 * - Bouton œil pour afficher/masquer le mot de passe
 * - 2 thèmes disponibles : 'dark' (Login transparent) et 'light' (Formulaires blancs)
 * - Validation visuelle (bordure rouge si erreur)
 * - Message d'erreur sous le champ
 * - Accessibilité complète (ARIA, focus management)
 * - Responsive (mobile, tablette, desktop)
 * - Styled Components (cohérent avec design système UCP)
 * 
 * @param {string} value - Valeur du champ
 * @param {function} onChange - Callback changement valeur
 * @param {string} variant - 'login' (auth transparent) ou 'app' (formulaires, suit le thème app)
 * @param {string} theme - Déprécié : 'dark' → login, 'light' → app
 * @param {string} placeholder - Texte placeholder (défaut: "Mot de passe")
 * @param {string} name - Attribut name du champ
 * @param {string} id - Attribut id du champ
 * @param {boolean} required - Champ obligatoire
 * @param {boolean} disabled - Champ désactivé
 * @param {string} autoComplete - Valeur autocomplete ('current-password' | 'new-password')
 * @param {string} className - Classes CSS additionnelles
 * @param {boolean} error - Afficher état erreur (bordure rouge)
 * @param {string} errorMessage - Message erreur à afficher sous le champ
 * 
 * @example
 * // Utilisation Login (theme dark - transparent bleu)
 * <PasswordInput
 *   theme="dark"
 *   value={credentials.password}
 *   onChange={(e) => setCredentials({...credentials, password: e.target.value})}
 *   placeholder="Votre mot de passe"
 *   autoComplete="current-password"
 *   required
 * />
 * 
 * @example
 * // Utilisation Formulaires (theme light - fond blanc)
 * <PasswordInput
 *   theme="light"
 *   value={passwordData.newPassword}
 *   onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
 *   placeholder="Nouveau mot de passe"
 *   autoComplete="new-password"
 *   error={errors.newPassword}
 *   errorMessage="Minimum 8 caractères requis"
 *   required
 * />
 */
const PasswordInput = ({
  value,
  onChange,
  variant,
  theme = 'dark',
  placeholder = 'Mot de passe',
  name,
  id,
  required = false,
  disabled = false,
  autoComplete = 'current-password',
  className = '',
  error = false,
  errorMessage = '',
  ...props
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const resolvedVariant = variant || (theme === 'dark' ? 'login' : 'app');

  const toggleVisibility = (e) => {
    e.preventDefault(); // Empêcher soumission formulaire si bouton cliqué
    setShowPassword(!showPassword);
  };
  
  return (
    <Container>
      <InputWrapper>
        <StyledInput
          $variant={resolvedVariant}
          type={showPassword ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          name={name}
          id={id}
          required={required}
          disabled={disabled}
          autoComplete={autoComplete}
          className={className}
          aria-invalid={error}
          aria-describedby={errorMessage ? `${id || name}-error` : undefined}
          {...props}
        />
        <ToggleButton
          $variant={resolvedVariant}
          type="button"
          onClick={toggleVisibility}
          disabled={disabled}
          aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
          title={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
        >
          {showPassword ? (
            // Œil ouvert (password visible)
            <svg viewBox="0 0 24 24">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          ) : (
            // Œil barré (password masqué)
            <svg viewBox="0 0 24 24">
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
              <line x1="1" y1="1" x2="23" y2="23" />
            </svg>
          )}
        </ToggleButton>
      </InputWrapper>
      {errorMessage && (
        <ErrorText 
          role="alert" 
          id={`${id || name}-error`}
        >
          {errorMessage}
        </ErrorText>
      )}
    </Container>
  );
};

export default PasswordInput;
